/**
 * Wholesaler Credit Management API
 * Phase 16: API client for wholesaler operations
 */

import apiClient from './client';

// ===== WHOLESALERS =====

/**
 * Get all wholesalers with filters and pagination
 */
export const getWholesalers = async (params = {}) => {
    const response = await apiClient.get('/wholesalers', { params });
    return response.data;
};

/**
 * Get single wholesaler by ID
 */
export const getWholesalerById = async (id) => {
    const response = await apiClient.get(`/wholesalers/${id}`);
    return response.data;
};

/**
 * Get wholesaler by customer ID
 */
export const getWholesalerByCustomerId = async (customerId) => {
    try {
        const response = await apiClient.get(`/wholesalers/customer/${customerId}`);
        return response.data;
    } catch (error) {
        // Return null if not found (customer is not a wholesaler)
        if (error.response?.status === 404) {
            return null;
        }
        throw error;
    }
};

/**
 * Get wholesaler statistics
 */
export const getWholesalerStatistics = async () => {
    const response = await apiClient.get('/wholesalers/statistics');
    return response.data;
};

/**
 * Check credit eligibility
 */
export const checkEligibility = async (id, amount = 0) => {
    const response = await apiClient.get(`/wholesalers/${id}/eligibility`, {
        params: { amount }
    });
    return response.data;
};

/**
 * Get transaction history
 */
export const getTransactions = async (id, params = {}) => {
    const response = await apiClient.get(`/wholesalers/${id}/transactions`, { params });
    return response.data;
};

/**
 * Get wholesaler alerts
 */
export const getWholesalerAlerts = async (id, params = {}) => {
    const response = await apiClient.get(`/wholesalers/${id}/alerts`, { params });
    return response.data;
};

// ===== ADMIN OPERATIONS =====

/**
 * Upgrade customer to wholesaler
 */
export const upgradeCustomer = async (data) => {
    const response = await apiClient.post('/wholesalers/upgrade', data);
    return response.data;
};

/**
 * Downgrade wholesaler to regular customer
 */
export const downgradeWholesaler = async (id, reason) => {
    const response = await apiClient.post(`/wholesalers/${id}/downgrade`, { reason });
    return response.data;
};

/**
 * Update credit limit
 */
export const updateCreditLimit = async (id, creditLimit, notes = null) => {
    const response = await apiClient.patch(`/wholesalers/${id}/credit-limit`, {
        creditLimit,
        notes
    });
    return response.data;
};

/**
 * Update wholesaler status
 */
export const updateStatus = async (id, status, reason = null) => {
    const response = await apiClient.patch(`/wholesalers/${id}/status`, {
        status,
        reason
    });
    return response.data;
};

/**
 * Toggle staff visibility
 */
export const toggleVisibility = async (id, staffCanViewBalance) => {
    const response = await apiClient.patch(`/wholesalers/${id}/visibility`, {
        staffCanViewBalance
    });
    return response.data;
};

// ===== TRANSACTIONS =====

/**
 * Process credit purchase
 */
export const processCreditPurchase = async (id, data) => {
    const response = await apiClient.post(`/wholesalers/${id}/credit-purchase`, data);
    return response.data;
};

/**
 * Process payment
 */
export const processPayment = async (id, data) => {
    const response = await apiClient.post(`/wholesalers/${id}/payments`, data);
    return response.data;
};

/**
 * Create balance adjustment
 */
export const createAdjustment = async (id, amount, reason) => {
    const response = await apiClient.post(`/wholesalers/${id}/adjustments`, {
        amount,
        reason
    });
    return response.data;
};

// ===== ALERTS =====

/**
 * Get all active alerts
 */
export const getActiveAlerts = async (params = {}) => {
    const response = await apiClient.get('/alerts/active', { params });
    return response.data;
};

/**
 * Get alert statistics
 */
export const getAlertStatistics = async () => {
    const response = await apiClient.get('/alerts/statistics');
    return response.data;
};

/**
 * Resolve an alert
 */
export const resolveAlert = async (id, notes = null) => {
    const response = await apiClient.patch(`/alerts/${id}/resolve`, { notes });
    return response.data;
};

/**
 * Run overdue detection (admin only)
 */
export const runOverdueDetection = async () => {
    const response = await apiClient.post('/alerts/run-overdue-detection');
    return response.data;
};

export default {
    getWholesalers,
    getWholesalerById,
    getWholesalerByCustomerId,
    getWholesalerStatistics,
    checkEligibility,
    getTransactions,
    getWholesalerAlerts,
    upgradeCustomer,
    downgradeWholesaler,
    updateCreditLimit,
    updateStatus,
    toggleVisibility,
    processCreditPurchase,
    processPayment,
    createAdjustment,
    getActiveAlerts,
    getAlertStatistics,
    resolveAlert,
    runOverdueDetection
};
